<template>
  <div>
    <header class="vm-page-header">
      <div>
        <h1>Financeiro</h1>
        <span class="vm-page-header__sub">
          Seus recebimentos por consulta — repasse de {{ comissionPercent }}%
          por atendimento realizado
        </span>
      </div>
      <div class="vm-page-header__actions">
        <select
          v-model="selectedMonth"
          class="select"
          style="width:160px;height:40px"
        >
          <option
            v-for="opt in monthOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <router-link
          :to="{ name: 'FinanceiroConta' }"
          class="btn btn--outline btn--sm"
        >
          Conta bancária
        </router-link>
      </div>
    </header>

    <div class="fin-body">
      <!-- Sub-navegação -->
      <div class="tabs">
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroDashboard' }"
          exact
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroExtrato' }"
        >
          Extrato
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroConta' }"
        >
          Conta bancária
        </router-link>
      </div>

      <!-- KPIs -->
      <div class="metrics4 fin-kpis">
        <template v-if="isLoading">
          <div v-for="i in 4" :key="i" class="metric">
            <div
              class="vm-skel"
              style="width:36px;height:36px;border-radius:8px;margin-bottom:8px"
            />
            <div
              class="vm-skel"
              style="width:80px;height:12px;margin-bottom:6px"
            />
            <div class="vm-skel" style="width:120px;height:28px" />
          </div>
        </template>
        <template v-else>
          <!-- Saldo disponível -->
          <div class="metric">
            <div
              class="metric-icon"
              style="background:var(--color-success-light);color:var(--color-success)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2v20M17 6H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <span class="metric-label">Saldo disponível</span>
            <span
              class="metric-value money--pos"
              style="font-size:var(--font-size-xl)"
            >
              {{ balanceFormatted }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              {{ creditCount }} consultas a repassar
            </span>
          </div>

          <!-- Receita do mês -->
          <div class="metric">
            <div
              class="metric-icon"
              style="background:var(--color-primary-light);color:var(--color-primary)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 17l5-5 4 3 7-8"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16 4h4v4"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <span class="metric-label">Receita do mês</span>
            <span class="metric-value" style="font-size:var(--font-size-xl)">
              {{ formatBRL(currentMonthRevenue * doctorFraction) }}
            </span>
            <span
              v-if="revenueTrend !== null"
              class="kpi-trend"
              :class="revenueTrend >= 0 ? 'kpi-trend--up' : 'kpi-trend--down'"
            >
              {{ revenueTrend >= 0 ? "▲" : "▼" }} {{ Math.abs(revenueTrend) }}%
              vs. mês anterior
            </span>
          </div>

          <!-- Próximo repasse -->
          <div class="metric">
            <div
              class="metric-icon"
              style="background:var(--color-accent-light);color:var(--color-accent)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="17"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M3 9h18M8 2v4M16 2v4"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </div>
            <span class="metric-label">Próximo repasse</span>
            <span class="metric-value" style="font-size:var(--font-size-xl)">
              {{ balanceFutureFormatted }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              {{ nextTransferDateLabel }}
            </span>
          </div>

          <!-- Consultas no mês -->
          <div class="metric">
            <div
              class="metric-icon"
              style="background:var(--color-gray-100);color:var(--color-gray-600)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <span class="metric-label">Consultas no mês</span>
            <span class="metric-value" style="font-size:var(--font-size-2xl)">
              {{ consultasNoMes }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              <strong style="color:var(--color-success)">
                {{ consultasNoMes }} pagas
              </strong>
            </span>
          </div>
        </template>
      </div>

      <!-- Grid principal -->
      <div class="fin-grid-main">
        <!-- Esquerda: gráfico de barras + resumo -->
        <div class="card" style="padding:var(--space-6)">
          <div
            class="row"
            style="justify-content:space-between;margin-bottom:var(--space-2)"
          >
            <h3
              style="font-size:var(--font-size-md);font-weight:var(--fw-bold);color:var(--color-gray-900);margin:0"
            >
              Evolução mensal · receita líquida
            </h3>
            <span class="badge badge--neutral badge--no-dot">
              últimos 6 meses
            </span>
          </div>

          <div class="bars">
            <div
              v-for="bar in monthlyBars"
              :key="bar.label"
              class="bar"
              :class="{ on: bar.isActive }"
            >
              <i :style="{ height: bar.height }" />
              <span>{{ bar.label }}</span>
            </div>
          </div>

          <hr
            style="border:none;border-top:1px solid var(--color-gray-100);margin:var(--space-5) 0"
          />

          <span class="fieldset-title">Resumo do mês</span>
          <div class="breakdown" style="margin-top:var(--space-2)">
            <div class="br">
              Valor bruto ({{ consultasNoMes }} consultas)
              <b class="money">{{ formatBRL(currentMonthRevenue) }}</b>
            </div>
            <div class="br">
              Taxa da plataforma ({{ platformPercent }}%)
              <b class="money" style="color:var(--color-danger)">
                − {{ formatBRL(currentMonthRevenue * platformFraction) }}
              </b>
            </div>
            <div class="br br--total">
              Receita líquida
              <b class="money money--pos">
                {{ formatBRL(currentMonthRevenue * doctorFraction) }}
              </b>
            </div>
          </div>
        </div>

        <!-- Direita: repasses recentes -->
        <div class="card card--flush">
          <div
            class="row"
            style="justify-content:space-between;padding:var(--space-5) var(--space-5) var(--space-3)"
          >
            <h3
              style="font-size:var(--font-size-md);font-weight:var(--fw-bold);color:var(--color-gray-900);margin:0"
            >
              Repasses recentes
            </h3>
            <router-link :to="{ name: 'FinanceiroExtrato' }" class="fin-link">
              Ver extrato →
            </router-link>
          </div>

          <div v-if="isLoading" class="fin-loading">
            <div v-for="i in 4" :key="i" class="fin-skel-row">
              <div
                class="vm-skel"
                style="flex:1;height:40px;border-radius:4px"
              />
              <div
                class="vm-skel"
                style="width:80px;height:40px;border-radius:4px"
              />
            </div>
          </div>

          <div
            v-else-if="recentTransfers.length === 0"
            class="vm-empty"
            style="padding:var(--space-8) var(--space-6)"
          >
            <p
              style="margin:0;font-size:var(--font-size-sm);color:var(--color-gray-400)"
            >
              Nenhuma movimentação encontrada.
            </p>
          </div>

          <table v-else class="table">
            <tbody>
              <tr
                v-for="(tx, i) in recentTransfers"
                :key="i"
                class="tr--link"
                @click="goToRepasse(tx.id, i)"
              >
                <td>
                  <div class="stack" style="gap:1px">
                    <span class="cell-strong">
                      {{ formatTransferDate(tx) }}
                    </span>
                    <span
                      style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                    >
                      {{ tx.orders_count || tx.count || "—" }} consultas
                    </span>
                  </div>
                </td>
                <td class="num t-right">
                  <div class="stack" style="gap:3px;align-items:flex-end">
                    <span class="money">{{ formatTransferAmount(tx) }}</span>
                    <span class="badge" :class="transferBadgeClass(tx.status)">
                      {{ transferStatusLabel(tx.status) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Footer: conta bancária -->
          <div
            style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100);display:flex;align-items:center;gap:var(--space-3)"
          >
            <div
              class="avatar avatar--sm"
              style="background:var(--color-primary-light);color:var(--color-primary)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect
                  x="2"
                  y="5"
                  width="20"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path d="M2 10h20" stroke="currentColor" stroke-width="2" />
              </svg>
            </div>
            <div class="stack" style="gap:0;flex:1;min-width:0">
              <span
                style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
              >
                Conta de destino
              </span>
              <span
                v-if="hasBankAccount"
                style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-800);overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
              >
                {{ bankAccountLabel }}
              </span>
              <span
                v-else
                style="font-size:var(--font-size-sm);color:var(--color-gray-400)"
              >
                Nenhuma conta cadastrada
              </span>
            </div>
            <router-link :to="{ name: 'FinanceiroConta' }" class="fin-link">
              Editar
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { pt as locale } from "date-fns/locale";
import { Doctor } from "../services/doctor";

export default {
  name: "FinanceiroDashboard",

  data() {
    return {
      selectedMonth: format(new Date(), "yyyy-MM"),
      comissionPercent: 80
    };
  },

  async mounted() {
    await Promise.all([
      this.$store.dispatch("doctor/getBankAccount"),
      this.$store.dispatch("doctor/getFinancials"),
      this.$store.dispatch("doctor/getBalances"),
      this.$store.dispatch("doctor/transfers")
    ]);
    Doctor.earnings()
      .then(res => {
        this.comissionPercent = res.data.comission_percent;
      })
      .catch(() => {});
  },

  methods: {
    goToRepasse(id, idx) {
      this.$router.push({
        name: "FinanceiroRepasse",
        params: { id: id ? String(id) : String(idx) }
      });
    },
    transferStatusLabel(status) {
      if (!status) return "—";
      const s = String(status).toLowerCase();
      if (s === "scheduled" || s === "agendado" || s === "pending")
        return "Agendado";
      return "Concluído";
    },
    transferBadgeClass(status) {
      if (!status) return "badge--neutral";
      const s = String(status).toLowerCase();
      if (s === "scheduled" || s === "agendado" || s === "pending")
        return "badge--warning";
      return "badge--success";
    },
    formatTransferDate(tx) {
      const raw =
        tx.transfer_date || tx.created_at || tx.date || tx.scheduled_at;
      if (!raw) return "—";
      try {
        return format(parseISO(raw), "dd/MM/yyyy");
      } catch {
        return raw;
      }
    },
    formatTransferAmount(tx) {
      const raw = tx.amount;
      if (!raw && raw !== 0) return "R$ 0,00";
      return this.formatBRL(Number(raw) / 100);
    },
    parseAmount(str) {
      if (!str) return 0;
      const cleaned = String(str)
        .replace(/[R$\s]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      return parseFloat(cleaned) || 0;
    },
    formatBRL(val) {
      if (!val) return "R$ 0,00";
      const str = val.toFixed(2).replace(".", ",");
      const parts = str.split(",");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return "R$ " + parts.join(",");
    }
  },

  computed: {
    doctorFraction() {
      return this.comissionPercent / 100;
    },
    platformFraction() {
      return 1 - this.comissionPercent / 100;
    },
    platformPercent() {
      return 100 - this.comissionPercent;
    },
    monthOptions() {
      const opts = [];
      const now = new Date();
      for (let i = 0; i < 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({
          value: format(d, "yyyy-MM"),
          label: format(d, "MMMM 'de' yyyy", { locale }).replace(/^\w/, c =>
            c.toUpperCase()
          )
        });
      }
      return opts;
    },
    isLoading() {
      return this.$store.getters["doctor/loading"];
    },
    balances() {
      return this.$store.state.doctor.balances;
    },
    balanceFormatted() {
      const gross = this.$store.getters["doctor/balances"].available;
      return this.formatBRL((gross * this.doctorFraction) / 100);
    },
    balanceFutureFormatted() {
      return this.$store.getters["doctor/balancesFormated"].future || "R$ 0,00";
    },
    financials() {
      return this.$store.state.doctor.financials;
    },
    transactions() {
      return this.financials.transactions || [];
    },
    recentTransfers() {
      return this.$store.state.doctor.transfer_history.slice(0, 4);
    },
    creditCount() {
      return this.transactions.filter(t => t.type === "crédito").length;
    },
    currentMonthStr() {
      return this.selectedMonth || format(new Date(), "yyyy-MM");
    },
    prevMonthStr() {
      const [y, m] = this.currentMonthStr.split("-").map(Number);
      const d = new Date(y, m - 2, 1);
      return format(d, "yyyy-MM");
    },
    currentMonthRevenue() {
      return this.transactions
        .filter(
          t =>
            t.type === "crédito" &&
            t.transaction_date?.startsWith(this.currentMonthStr)
        )
        .reduce((sum, t) => sum + (t.amount || 0) / 100, 0);
    },
    prevMonthRevenue() {
      return this.transactions
        .filter(
          t =>
            t.type === "crédito" &&
            t.transaction_date?.startsWith(this.prevMonthStr)
        )
        .reduce((sum, t) => sum + (t.amount || 0) / 100, 0);
    },
    revenueTrend() {
      if (this.prevMonthRevenue === 0) return null;
      return parseFloat(
        (
          ((this.currentMonthRevenue - this.prevMonthRevenue) /
            this.prevMonthRevenue) *
          100
        ).toFixed(1)
      );
    },
    consultasNoMes() {
      return this.transactions.filter(
        t =>
          t.type === "crédito" &&
          t.transaction_date?.startsWith(this.currentMonthStr)
      ).length;
    },
    nextTransferDateLabel() {
      const next = this.$store.state.doctor.transfer_history.find(t => {
        const s = String(t.status || "").toLowerCase();
        return s === "scheduled" || s === "agendado" || s === "pending";
      });
      if (next) {
        const raw = next.transfer_date || next.created_at || next.date;
        if (raw) {
          try {
            return format(parseISO(raw), "EEEE, dd/MM", { locale });
          } catch {
            return "a receber";
          }
        }
      }
      return "a receber";
    },
    monthlyBars() {
      const bars = [];
      const [y, m] = this.currentMonthStr.split("-").map(Number);
      for (let i = 5; i >= 0; i--) {
        const d = new Date(y, m - 1 - i, 1);
        const monthStr = format(d, "yyyy-MM");
        const label = format(d, "MMM", { locale });
        const amount = this.transactions
          .filter(
            t =>
              t.type === "crédito" && t.transaction_date?.startsWith(monthStr)
          )
          .reduce(
            (sum, t) => sum + ((t.amount || 0) / 100) * this.doctorFraction,
            0
          );
        bars.push({
          label: label.charAt(0).toUpperCase() + label.slice(1),
          amount,
          isActive: i === 0
        });
      }
      const maxAmount = Math.max(...bars.map(b => b.amount), 1);
      return bars.map(b => ({
        ...b,
        height: Math.round((b.amount / maxAmount) * 92) + 8 + "%"
      }));
    },
    bankAccount() {
      return this.$store.state.doctor.bank_account;
    },
    hasBankAccount() {
      return !!this.bankAccount.id;
    },
    bankAccountLabel() {
      if (!this.hasBankAccount) return "";
      const ag = this.bankAccount.agency_number
        ? `Ag. ${this.bankAccount.agency_number}`
        : "";
      const cc = this.bankAccount.account_number
        ? `CC ${this.bankAccount.account_number}`
        : "";
      return [this.bankAccount.bank_name, ag, cc].filter(Boolean).join(" · ");
    }
  }
};
</script>

<style lang="scss" scoped>
.fin-body {
  padding: var(--space-6) var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

// neutraliza margin-bottom do .tabs global — espaçamento vem do gap do fin-body
.tabs {
  margin-bottom: 0;
}

.fin-kpis {
  margin-bottom: 0;
}

.fin-grid-main {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.fin-link {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.fin-loading {
  padding: var(--space-4) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.fin-skel-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
</style>
